/**
 * Toggleable diagnostic logger for the AI pipeline.
 *
 * Gated behind `window.__FINSTER_DIAG__` or `localStorage["finster:diag"]`.
 * Ships dark — no-op unless explicitly enabled at runtime (dev tools console:
 *   `window.__FINSTER_DIAG__ = true` or `localStorage.setItem("finster:diag","1")`).
 *
 * Channels: date, api, tool, llm, write, format, skill.
 * Each entry: { channel, event, ts, traceId?, stepNumber?, fields }.
 *
 * Temporary — will be removed after fixing the TTF/formatting/date issues.
 */

export type DiagChannel = "date" | "api" | "tool" | "llm" | "write" | "format" | "skill" | "perf";

interface DiagEntry {
  channel: DiagChannel;
  event: string;
  ts: string;
  traceId?: string;
  stepNumber?: number;
  fields: Record<string, unknown>;
}

const MAX_FIELD_LENGTH = 4096;

let _traceId: string | undefined;
let _stepNumber: number | undefined;

export function isDiagEnabled(): boolean {
  try {
    if (typeof window !== "undefined" && (window as never)["__FINSTER_DIAG__"]) return true;
    if (typeof localStorage !== "undefined" && localStorage.getItem("finster:diag")) return true;
  } catch {
    // SSR / test env — silent
  }
  return false;
}

function truncate(value: unknown): unknown {
  if (typeof value === "string" && value.length > MAX_FIELD_LENGTH) {
    return value.slice(0, MAX_FIELD_LENGTH) + " [truncated]";
  }
  if (typeof value === "object" && value !== null) {
    try {
      const str = JSON.stringify(value);
      if (str.length > MAX_FIELD_LENGTH) {
        return str.slice(0, MAX_FIELD_LENGTH) + " [truncated]";
      }
    } catch {
      return "[unserializable]";
    }
  }
  return value;
}

function sanitizeFields(fields: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    out[k] = truncate(v);
  }
  return out;
}

export function setTraceId(id: string): void {
  _traceId = id;
}

export function setStepNumber(n: number | undefined): void {
  _stepNumber = n;
}

function getTraceId(): string | undefined {
  return _traceId;
}

function clearTrace(): void {
  _traceId = undefined;
  _stepNumber = undefined;
}

/**
 * Emit a diagnostic log entry. No-op unless diagnostics are enabled.
 */
export function diag(channel: DiagChannel, event: string, fields: Record<string, unknown> = {}): void {
  if (!isDiagEnabled()) return;
  const entry: DiagEntry = {
    channel,
    event,
    ts: new Date().toISOString(),
    ...(_traceId ? { traceId: _traceId } : {}),
    ...(_stepNumber !== undefined ? { stepNumber: _stepNumber } : {}),
    fields: sanitizeFields(fields),
  };
  try {
    console.log(`[DIAG:${channel}] ${event}`, entry);
  } catch {
    // best-effort
  }
}

// ---------------------------------------------------------------------------
// Phase timing telemetry (p50/p95)
// ---------------------------------------------------------------------------

/**
 * In-memory per-phase latency samples for p50/p95 analysis across repeated
 * runs within a single Excel session. Cheap: a bounded number array per phase.
 *
 * Gated by diagnostics — recording is a no-op unless diag is enabled, so this
 * ships dark with zero overhead in production. To capture a baseline: enable
 * diagnostics, run the target flow N times, then read the aggregated stats:
 *
 *   window.__FINSTER_DIAG__ = true
 *   window.__FINSTER_PHASE_RESET__()   // optional: clear prior samples
 *   // ...run the flow N times...
 *   console.table(window.__FINSTER_PHASE_STATS__())
 */
const MAX_SAMPLES_PER_PHASE = 1000;
const _phaseSamples = new Map<string, number[]>();

/**
 * Record one latency sample (ms) for a named phase and emit a `perf` diag line.
 * No-op unless diagnostics are enabled.
 */
export function recordPhase(phase: string, ms: number, fields: Record<string, unknown> = {}): void {
  if (!isDiagEnabled()) return;
  let samples = _phaseSamples.get(phase);
  if (!samples) {
    samples = [];
    _phaseSamples.set(phase, samples);
  }
  samples.push(ms);
  if (samples.length > MAX_SAMPLES_PER_PHASE) samples.shift();
  diag("perf", phase, { ms: Math.round(ms), ...fields });
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil((p / 100) * sorted.length) - 1),
  );
  return sorted[idx];
}

export interface PhaseStat {
  phase: string;
  count: number;
  p50: number;
  p95: number;
  min: number;
  max: number;
  mean: number;
}

/** Aggregate recorded phase samples into count/p50/p95/min/max/mean rows. */
export function getPhaseStats(): PhaseStat[] {
  const out: PhaseStat[] = [];
  // `forEach` (not `for…of` over `.entries()`) so this compiles under the
  // project's `target: es5` tsconfig without `--downlevelIteration`.
  _phaseSamples.forEach((samples, phase) => {
    if (samples.length === 0) return;
    const sorted = samples.slice().sort((a, b) => a - b);
    const sum = sorted.reduce((acc, n) => acc + n, 0);
    out.push({
      phase,
      count: sorted.length,
      p50: Math.round(percentile(sorted, 50)),
      p95: Math.round(percentile(sorted, 95)),
      min: Math.round(sorted[0]),
      max: Math.round(sorted[sorted.length - 1]),
      mean: Math.round(sum / sorted.length),
    });
  });
  return out.sort((a, b) => a.phase.localeCompare(b.phase));
}

/** Clear all recorded phase samples (e.g. before capturing a fresh baseline). */
export function resetPhaseStats(): void {
  _phaseSamples.clear();
}

// ---------------------------------------------------------------------------
// Success-criteria bars
// ---------------------------------------------------------------------------

/**
 * Explicit latency bars agreed for the latency work. Each bar names the phase
 * whose p95 it grades, a `target` (the bar we must clear) and an optional
 * `stretch` (the better-than-target goal). Thresholds are in milliseconds.
 *
 * The primary bar is end-to-end wall-clock:
 *   - comps / DCF build p95 < 3min (stretch 2min) → `run:total`
 */
export interface SuccessCriterion {
  id: string;
  label: string;
  phase: string;
  fallbackPhase?: string;
  metric: "p95" | "p50";
  targetMs: number;
  stretchMs?: number;
}

export const SUCCESS_CRITERIA: SuccessCriterion[] = [
  {
    id: "build_p95",
    label: "Comps / DCF build (p95 wall-clock)",
    phase: "run:total",
    metric: "p95",
    targetMs: 180_000,
    stretchMs: 120_000,
  },
];

export interface SuccessCriterionResult {
  id: string;
  label: string;
  phase: string;
  count: number;
  observedMs: number | null;
  targetMs: number;
  stretchMs: number | null;
  status: "pass-stretch" | "pass" | "fail" | "no-data";
}

/**
 * Grade the recorded phase samples against {@link SUCCESS_CRITERIA}. Returns one
 * row per bar with the observed metric, the target, and a pass/fail/no-data
 * status. Console-friendly: `console.table(window.__FINSTER_SUCCESS__())`.
 */
export function evaluateSuccessCriteria(): SuccessCriterionResult[] {
  const stats = getPhaseStats();
  const byPhase = new Map(stats.map((s) => [s.phase, s]));
  return SUCCESS_CRITERIA.map((c) => {
    const stat = byPhase.get(c.phase) ?? (c.fallbackPhase ? byPhase.get(c.fallbackPhase) : undefined);
    if (!stat || stat.count === 0) {
      return {
        id: c.id,
        label: c.label,
        phase: c.phase,
        count: 0,
        observedMs: null,
        targetMs: c.targetMs,
        stretchMs: c.stretchMs ?? null,
        status: "no-data" as const,
      };
    }
    const observedMs = c.metric === "p50" ? stat.p50 : stat.p95;
    let status: SuccessCriterionResult["status"];
    if (c.stretchMs !== undefined && observedMs <= c.stretchMs) status = "pass-stretch";
    else if (observedMs <= c.targetMs) status = "pass";
    else status = "fail";
    return {
      id: c.id,
      label: c.label,
      phase: stat.phase,
      count: stat.count,
      observedMs,
      targetMs: c.targetMs,
      stretchMs: c.stretchMs ?? null,
      status,
    };
  });
}

/**
 * Time an async function as a named phase. Always runs `fn`; records the
 * elapsed wall-clock (and re-throws on error) only when diagnostics are on.
 */
export async function timePhase<T>(
  phase: string,
  fn: () => Promise<T>,
  fields: Record<string, unknown> = {},
): Promise<T> {
  const startedAt = Date.now();
  let ok = true;
  try {
    return await fn();
  } catch (err) {
    ok = false;
    throw err;
  } finally {
    recordPhase(phase, Date.now() - startedAt, { ok, ...fields });
  }
}

// Expose for manual inspection from the dev console.
try {
  if (typeof window !== "undefined") {
    const w = window as never as Record<string, unknown>;
    w["__FINSTER_PHASE_STATS__"] = getPhaseStats;
    w["__FINSTER_PHASE_RESET__"] = resetPhaseStats;
    w["__FINSTER_SUCCESS__"] = evaluateSuccessCriteria;
  }
} catch {
  // SSR / test env — silent
}
